/**
 * @file table-search-input.component.tsx
 * @description Компонент поля поиска по таблице для Task Manager приложения, который обеспечивает автоматическую фокусировку и ввод символов в модальных окнах. Компонент реагирует на ввод символов и backspace, проверяет отсутствие фокуса на других input элементах, работает только в активном (последнем) модальном окне. При соблюдении условий автоматически фокусируется и добавляет введенный символ в строку поиска, обеспечивая плавный пользовательский опыт поиска по таблицам.
 * @author AI-Generated | Human-Reviewed
 * @created 2025-01-15
 * @updated 2025-09-18
 * @dependencies ['react', '@components/ui/base/text-input', '@features/catalog/context/search-text-context']
 * @related-files ['src/components/ui/base/text-input/text-input.component.tsx', 'src/features/catalog/context/search-text-context.tsx']
 */

import { useEffect, useRef, useCallback } from 'react';
import { TextInput } from '@components/ui/base/text-input';
import { useSearchText } from '@features/catalog/context/search-text-context';

interface IInputElement extends Element {
    tagName: 'INPUT' | 'TEXTAREA' | 'SELECT';
    type?: string;
    readOnly?: boolean;
    disabled?: boolean;
}

interface IModalWindow extends Element {
    classList: DOMTokenList;
}

export const TableSearchInput = () => {
    const inputRef = useRef<HTMLInputElement>(null);
    const { searchText, setSearchText } = useSearchText();

    const isInputElement = (element: Element): element is IInputElement => {
        const tagName = element.tagName as string;
        return ['INPUT', 'TEXTAREA', 'SELECT'].includes(tagName);
    };

    const isEditableElement = (element: Element): boolean => {
        if (!isInputElement(element)) return false;

        const inputElement = element as IInputElement;
        return !inputElement.readOnly && !inputElement.disabled;
    };

    const hasActiveInputFocus = (): boolean => {
        const activeElement = document.activeElement;
        return activeElement ? isEditableElement(activeElement) : false;
    };

    const getLastActiveModal = (): IModalWindow | null => {
        const modals = document.querySelectorAll('.Modal-Window') as NodeListOf<IModalWindow>;
        return modals.length > 0 ? modals[modals.length - 1] : null;
    };

    const isModalActive = (): boolean => {
        return getLastActiveModal() !== null;
    };

    const focusSearchInput = (): boolean => {
        if (!isModalActive()) {
            if (inputRef.current && !hasActiveInputFocus()) {
                inputRef.current.focus();
                return true;
            }
            return false;
        }

        const activeModal = getLastActiveModal();
        if (activeModal) {
            const searchInput = activeModal.querySelector('.table-search-input input') as HTMLInputElement;
            if (searchInput && !hasActiveInputFocus()) {
                searchInput.focus();
                return true;
            }
        }
        return false;
    };

    const handleKeyPress = useCallback(
        (event: KeyboardEvent) => {
            if (!event.key) return;

            const isCharacterKey = event.key.length === 1 && !event.ctrlKey && !event.altKey && !event.metaKey;
            const isBackspace = event.key === 'Backspace';

            if (!isCharacterKey && !isBackspace) return;

            if (hasActiveInputFocus()) return;

            const focused = focusSearchInput();
            if (!focused) return;

            if (isCharacterKey) {
                event.preventDefault();
                setSearchText(searchText + event.key);
            } else if (isBackspace && searchText.length > 0) {
                event.preventDefault();
                setSearchText(searchText.slice(0, -1));
            }
        },
        [searchText, setSearchText]
    );

    useEffect(() => {
        document.addEventListener('keydown', handleKeyPress);
        return () => document.removeEventListener('keydown', handleKeyPress);
    }, [handleKeyPress]);

    const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
        event.target.select();
    };

    return (
        <TextInput
            ref={inputRef}
            size="s"
            placeholder="Поиск"
            value={searchText}
            onChange={(val: string | null) => setSearchText(val || '')}
            onFocus={handleFocus}
            className="table-search-input w-[26.4128vmin]"
            autoFocus={false}
            withClearButton={false}
        />
    );
};
